import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { FormError } from '@/components/FormError'
import {
  BookBuilder,
  Choice,
  Kept,
  LookTile,
  NoPageEdit,
  PageEdit,
  Section,
  YourLook,
  type Trim,
} from '@/components/print/Builder'
import { MakeFiles } from '@/components/print/MakeFiles'
import { MarkedPage } from '@/components/print/MarkedPage'
import {
  COOKBOOK_LOOK_IDS,
  COOKBOOK_LOOKS,
  sameCookbookLook,
  CLASSIC_COOKBOOK,
  readChosenCookbookLook,
  type ChosenCookbookLook,
  type CookbookLook,
  type RecipeEdit,
  type RecipeEdits,
} from '@/lib/cookbook/looks'
import type { CookbookPreview } from '@/lib/cookbook/pdf'
import { useCookbook, type Cookbook } from '@/lib/cookbooks'
import { usePageFrame } from '@/lib/frame'
import { printableCookbook } from '@/lib/print/build'
import { editPage, useDesign } from '@/lib/print/design'
import { FINISHES, SCREEN } from '@/lib/print/finishes'
import type { Layout } from '@/lib/print/kit'
import { formatById } from '../../shared/print'
import { ScreenLoader } from '@/components/Loader'

export function PrintCookbookPage() {
  const { id = '' } = useParams()
  const cookbook = useCookbook(id)
  if (cookbook.isPending) return <ScreenLoader surface="table" />
  if (cookbook.isError) return <FormError error={cookbook.error} />
  return <Builder cookbook={cookbook.data} />
}

// The book's pages in a look, drawn by the PDF's own code on marks (cookbook/pdf.ts), once its fonts have loaded.
function usePreview(cookbook: Cookbook, layout: Layout, looks: CookbookLook[], edits?: RecipeEdits) {
  const [state, setState] = useState<{ previews: CookbookPreview[] } | { error: Error }>()
  const key = JSON.stringify([layout, looks, edits])
  useEffect(() => {
    let live = true
    import('@/lib/cookbook/pdf')
      .then(({ cookbookPreview }) => Promise.all(looks.map((look) => cookbookPreview(cookbook, layout, look, edits))))
      .then(
        (previews) => live && setState({ previews }),
        (e: unknown) => {
          console.error(e)
          if (live) setState({ error: new Error('The pages couldn’t be drawn. Please reload to try again.') })
        },
      )
    return () => {
      live = false
    }
    // The key stands for the layout, looks and edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookbook, key])
  // The last pages drawn stay up while a change is drawn, so the book doesn't blink.
  return state
}

// The cookbook builder: pick a look, change anything about it, and turn the pages of the book that will be printed.
// The pages turned here are drawn by the same code that draws the PDF, so what's turned is what's printed.
function Builder({ cookbook }: { cookbook: Cookbook }) {
  usePageFrame(cookbook.familyId, {
    title: 'Print this book',
    back: { to: `/cookbook/${cookbook.id}`, label: 'Back to the cookbook' },
  })
  const {
    design: chosen,
    choose,
    keeping,
  } = useDesign<ChosenCookbookLook>({
    book: { kind: 'cookbook', id: cookbook.id },
    storageKey: `gf.cookbook-look.${cookbook.id}`,
    kept: cookbook.printDesign ?? null,
    mine: cookbook.mine,
    read: readChosenCookbookLook,
    start: { from: 'classic', look: CLASSIC_COOKBOOK },
  })
  const [finish, setFinish] = useState('pdf')
  const [busy, setBusy] = useState(false)
  const look = chosen.look
  const format = formatById(finish)
  const layout: Layout = format ? { trim: format.trim, minPages: format.minPages, bound: true } : SCREEN
  const trim = layout.trim
  const custom = !sameCookbookLook(look, COOKBOOK_LOOKS[chosen.from].look)
  const drawn = usePreview(cookbook, layout, [look], chosen.pages)
  const tiles = usePreview(
    cookbook,
    layout,
    COOKBOOK_LOOK_IDS.map((id) => COOKBOOK_LOOKS[id].look),
  )

  const change = <K extends keyof CookbookLook>(key: K, value: CookbookLook[K]) =>
    choose({ ...chosen, look: { ...look, [key]: value } })
  // A recipe's own choices, kept by its id.
  const editOne = (id: string, edit: RecipeEdit) => choose({ ...chosen, pages: editPage(chosen.pages, id, edit) })

  const preview = drawn && 'previews' in drawn ? drawn.previews[0]! : null
  const shown = preview?.look ?? look
  // The whole book: its cover, the inside of the cover, the printed pages, and the back.
  const page = (marks: CookbookPreview['pages'][number]) => () => (
    <MarkedPage marks={marks} trim={trim} paper={shown.paper} />
  )
  const blank = () => <MarkedPage marks={[]} trim={trim} paper={shown.paper} />
  const faces = preview
    ? [page(preview.cover), blank, ...preview.pages.map(page), blank, page(preview.back)]
    : [blank, blank]

  return (
    <BookBuilder
      label={`Print ${cookbook.title}, cookbook`}
      badge="Cookbook"
      back={{ to: `/cookbook/${cookbook.id}`, label: 'the cookbook' }}
      trim={trim}
      photos={look.photos}
      faces={faces}
      pages={preview?.pages.length ?? 0}
      thisPage={(shown) => {
        // The recipes on the pages showing, each once: a picture and its recipe face each other.
        const ids = [...new Set(shown.flatMap((i) => preview?.recipes[i - 2] ?? []))]
        const recipes = ids.flatMap((id) => cookbook.pages.filter((x) => x.recipe.id === id).slice(0, 1))
        if (!recipes.length) return <NoPageEdit>Turn to a recipe to change just that one.</NoPageEdit>
        return recipes.map(({ recipe }) => {
          const edit = chosen.pages?.[recipe.id] ?? {}
          return (
            <PageEdit
              key={recipe.id}
              name={recipe.title}
              edited={Boolean(chosen.pages?.[recipe.id])}
              onReset={() => editOne(recipe.id, {})}
            >
              <Choice<CookbookLook['layout'] | ''>
                label="Picture"
                value={edit.layout ?? ''}
                options={[
                  ['', 'As the book'],
                  ['facing', 'Facing'],
                  ['one', 'Above'],
                ]}
                onChange={(v) => editOne(recipe.id, { ...edit, layout: v || undefined })}
              />
              <Choice<CookbookLook['pictures'] | ''>
                label="Edges"
                value={edit.pictures ?? ''}
                options={[
                  ['', 'As the book'],
                  ['bleed', 'Edge to edge'],
                  ['framed', 'Framed'],
                ]}
                onChange={(v) => editOne(recipe.id, { ...edit, pictures: v || undefined })}
              />
            </PageEdit>
          )
        })
      }}
    >
      {drawn && 'error' in drawn && (
        <div className="px-5 pt-5">
          <FormError error={drawn.error} />
        </div>
      )}
      <Section title="Start with a look">
        <div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label="Looks">
          {COOKBOOK_LOOK_IDS.map((id, i) => (
            <LookTile
              key={id}
              name={COOKBOOK_LOOKS[id].name}
              blurb={COOKBOOK_LOOKS[id].blurb}
              checked={chosen.from === id && !custom}
              onPick={() => choose({ ...chosen, from: id, look: COOKBOOK_LOOKS[id].look })}
            >
              {tiles && 'previews' in tiles && (
                <Sample preview={tiles.previews[i]!} look={COOKBOOK_LOOKS[id].look} trim={trim} />
              )}
            </LookTile>
          ))}
        </div>
        {custom && (
          <YourLook
            from={COOKBOOK_LOOKS[chosen.from].name}
            onReset={() => choose({ ...chosen, look: COOKBOOK_LOOKS[chosen.from].look })}
          />
        )}
        <Kept mine={cookbook.mine} keeping={keeping} noun="cookbook" by={cookbook.by} />
      </Section>

      <Section title="Customize">
        <Choice
          label="Pictures"
          value={look.layout}
          options={[
            ['facing', 'Facing the recipe'],
            ['one', 'Above the recipe'],
          ]}
          onChange={(v) => change('layout', v)}
        />
        <Choice
          label="Edges"
          value={look.pictures}
          options={[
            ['bleed', 'Edge to edge'],
            ['framed', 'Framed'],
          ]}
          onChange={(v) => change('pictures', v)}
        />
        <Choice
          label="Type"
          value={look.type}
          options={[
            ['traditional', 'Traditional'],
            ['modern', 'Modern'],
          ]}
          onChange={(v) => change('type', v)}
        />
        <Choice
          label="Paper"
          value={look.paper}
          options={[
            ['white', 'White'],
            ['natural', 'Natural'],
          ]}
          onChange={(v) => change('paper', v)}
        />
        <Choice
          label="Photo color"
          hint="How the photos print. The photos in the app stay as they are."
          value={look.photos}
          options={[
            ['color', 'Color'],
            ['mono', 'Black and white'],
            ['sepia', 'Sepia'],
          ]}
          onChange={(v) => change('photos', v)}
        />
        <Choice
          label="Cover"
          value={look.cover}
          options={[
            ['plate', 'Plate'],
            ['photo', 'Photo'],
            ['words', 'Words'],
          ]}
          onChange={(v) => change('cover', v)}
        />
        {look.pictures === 'bleed' && (
          <p className="text-sub text-muted-foreground">
            Pictures are cropped to fill their space on the page. The photos in your recipes stay as they are.
          </p>
        )}
      </Section>

      <Section title="Size and finish">
        <Choice
          label="Make it as"
          value={finish}
          disabled={busy}
          options={FINISHES.map((x) => [x.id, x.label] as [string, string])}
          onChange={setFinish}
        />
        <p className="text-sub text-muted-foreground">
          {FINISHES.find((x) => x.id === finish)!.hint}.
          {preview &&
            ` ${preview.pages.length} pages${
              format && preview.added > 0
                ? `, with ${preview.added} ruled ${preview.added === 1 ? 'page' : 'pages'} for notes to reach the ${format.binding}’s ${format.minPages}.`
                : '.'
            }`}
        </p>
        <MakeFiles
          key={`${finish}:${JSON.stringify(chosen)}`}
          book={printableCookbook(cookbook, look, chosen.pages)}
          formatId={finish}
          filler="More recipes or a dedication"
          onBusy={setBusy}
        />
      </Section>
    </BookBuilder>
  )
}

// A look's tile: the book's cover and the first recipe's opening page in it.
function Sample({ preview, look, trim }: { preview: CookbookPreview; look: CookbookLook; trim: Trim }) {
  const opening = preview.pages[preview.opening] ?? []
  return (
    <span className="flex h-full gap-px shadow-paper">
      {[preview.cover, opening].map((marks, i) => (
        <span key={i} className="relative block h-full" style={{ aspectRatio: `${trim.width} / ${trim.height}` }}>
          <MarkedPage marks={marks} trim={trim} paper={look.paper} />
        </span>
      ))}
    </span>
  )
}
